import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { FiltersTownHalls } from 'system/components/town-halls/list';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { TownHall } from 'shell/models/town-hall';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesTownHalls } from '..';
import { useFormik } from 'formik';
import { FiltersFormTownHall, TownHallClient } from './types';
import { transformDataset } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonTownHallCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaTownFilter } from './constants';
import ListHeader from 'shell/components/shared/list-header';
import { Typography } from '@mui/material';

const TownHallsPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<TownHallClient>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<TownHallClient>>({
    element: null,
    open: false,
  });
  const { delete: deleteTownHall } = useHttpFetch(`${__TOPONYMY_API_URL__}/municipi`);

  const filterTable = useFilterTable<FiltersFormTownHall>();

  const {
    data,
    get: getTownHalls,
    error,
  } = useHttpFetch<HttpResponseList<TownHall>>(`${__TOPONYMY_API_URL__}/municipi`, {
    data: {
      payload: {
        list: [],
        pageInfo: {
          totalCount: 0,
        },
      },
    },
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormTownHall) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterTownHallForm.setFieldValue('page', 0);
      getTownHalls(queryString);
    },
    [getTownHalls]
  );

  const townHalls: Array<TownHallClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((townHall) => ({
      description: townHall.descrizioneBreve,
      code: townHall.key.codice,
    }));
  }, [data, error]);

  const filterTownHallForm = useFormik({
    initialValues: initialValuesTownHalls,
    validationSchema: validationSchemaTownFilter,
    onSubmit,
  });

  useEffect(() => {
    filterTownHallForm.setValues(filterTable.values || initialValuesTownHalls);
    const queryString = transformDataset(filterTable.values || initialValuesTownHalls);
    getTownHalls(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterTownHallForm.values);
  }, [filterTownHallForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.pageInfo) {
      return 0;
    }
    return data.payload.pageInfo?.totalCount;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<TownHallClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/municipi/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<TownHallClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/municipi/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<TownHallClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<TownHallClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterTownHallForm.setFieldValue('page', newPage);
    },
    [filterTownHallForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterTownHallForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterTownHallForm.setValues(newDataset);
    },
    [filterTownHallForm.setFieldValue, filterTownHallForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterTownHallForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getTownHalls(queryString).then(() => {
        filterTownHallForm.setFieldValue('orders', orders);
      });
    },
    [filterTownHallForm.setFieldValue, getTownHalls, filterTownHallForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonTownHallCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo municipio/zona"
        addBtnLabel="Aggiungi nuovo municipio/zona"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/municipi/nuovo');
        }}
        title="Gestione Municipi/Zone"
      />
      <div className="pmi-my-3">
        <FiltersTownHalls filterTownHallForm={filterTownHallForm} />
      </div>
      {data?.payload ? (
        townHalls?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterTownHallForm.values.orders}
            activeActions={auths}
            page={filterTownHallForm.values.page}
            rowsPerPage={filterTownHallForm.values.rowsPerPage}
            body={townHalls}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Municipi"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Municipio/Zona"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteTownHall(`/${deleteAlertModal.element?.code}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Municipio/Zona"
        message={`La cancellazione del municipio ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterTownHallForm.values, page: 0 });
          getTownHalls(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterTownHallForm.values, page: 0 });
          getTownHalls(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

TownHallsPage.displayName = 'Town halls page component';

export default TownHallsPage;
